import React, { useEffect, useMemo, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import DocumentMasterReportTable from "../../common/documentMaster/DocumentMasterReportTable";
import {
  isDocumentMasterRowUsed,
  QMS_DOCUMENT_CATEGORY_IN_USE_MESSAGE,
} from "../../common/documentMaster/documentMasterUtils";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import ConfirmModal from "../../common/ConfirmModal";
import {
  fetchDocumentCategories,
  postDocumentCategory,
  deleteDocumentCategory,
} from "./qmsService";
import {
  normalizeSuccessStoryList,
  firstSuccessStoryRow,
  isApiSuccess,
  apiMessage,
} from "./qmsApiUtils";
import { getQmsAccessState } from "./qmsPermissionUtils";
import {
  useDocumentMasterAbbreviationSuggest,
  QMS_DOCUMENT_ABBR_MAX_LENGTH,
} from "../../../hooks/useDocumentMasterAbbreviationSuggest";
import "./QmsPages.scss";

const alphaNumRegex = /^[a-zA-Z0-9 ]+$/;

const QmsDocumentCategories = ({ embedded = false }) => {
  const { userData, loading: userLoading } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const isSystemAdmin = userData?.Role === "System Administrator";

  const access = getQmsAccessState({
    userData,
    isSystemAdmin,
    permissionKey: "QMS_DOCUMENT_CATEGORY",
    loading: userLoading,
  });

  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [errors, setErrors] = useState({});
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    row: null,
  });

  const layoutStorageKey = useMemo(
    () =>
      `qms-doc-category-layout-v1:${String(userData?.OrganizationId || "org").toLowerCase()}`,
    [userData?.OrganizationId],
  );

  const { resetSuggestState, handleAbbreviationChange, abbrTouched } =
    useDocumentMasterAbbreviationSuggest({
      nameValue: categoryName,
      showModal,
      setAbbreviation,
      maxLength: QMS_DOCUMENT_ABBR_MAX_LENGTH,
    });

  const resetForm = () => {
    setEditingRow(null);
    setCategoryName("");
    setAbbreviation("");
    setErrors({});
    resetSuggestState(false);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    setCategoryName(row.DocumentCategory || "");
    setAbbreviation(row.Abberiviation || "");
    setErrors({});
    resetSuggestState(true);
    setShowModal(true);
  };

  const validate = () => {
    const next = {};
    if (!categoryName.trim()) next.categoryName = "Category is required.";
    else if (!alphaNumRegex.test(categoryName.trim()))
      next.categoryName = "Only letters, numbers and spaces are allowed.";
    else if (categoryName.trim().length > 110)
      next.categoryName = "Maximum 110 characters allowed.";

    if (!abbreviation.trim()) next.abbreviation = "Abbreviation is required.";
    else if (!alphaNumRegex.test(abbreviation.trim()))
      next.abbreviation = "Only letters, numbers and spaces are allowed.";
    else if (abbreviation.trim().length > 10)
      next.abbreviation = "Maximum 10 characters allowed.";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const loadCategories = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    try {
      setListLoading(true);
      const response = await fetchDocumentCategories(userData);
      setRows(normalizeSuccessStoryList(response));
    } catch (error) {
      console.error("Error loading document categories", error);
      showAlert({
        title: "Error",
        message: error?.message || "Failed to load document categories.",
        type: "danger",
      });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (
      access.status === "allowed" &&
      userData?.OrganizationId &&
      userData?.StaffId
    ) {
      loadCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.status, userData?.OrganizationId, userData?.StaffId]);

  const handleSave = async () => {
    if (!validate() || !userData?.OrganizationId || !userData?.StaffId) return;

    const actionType = editingRow
      ? "UpdateDocumentCategory"
      : "SaveDocumentCategory";
    const body = {
      ActionType: actionType,
      DocumentCategory: categoryName.trim(),
      Abberiviation: abbreviation.trim(),
      DocumentCategoryId: editingRow?.DocumentCategoryId || "",
      StaffRoles: editingRow ? "" : "",
    };

    try {
      showLoader(editingRow ? "Updating category..." : "Saving category...");
      const response = await postDocumentCategory(userData, body);
      const row = firstSuccessStoryRow(response);

      if (isApiSuccess(response, row)) {
        showAlert({
          title: "Success",
          message:
            apiMessage(response, row) ||
            (editingRow
              ? "Document category has been updated successfully."
              : "Document category has been saved successfully."),
          type: "success",
        });
        setShowModal(false);
        resetForm();
        await loadCategories();
      } else {
        showAlert({
          title: "Error",
          message:
            apiMessage(response, row) || "Could not save document category.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error saving document category", error);
      showAlert({
        title: "Error",
        message: error?.message || "Failed to save document category.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleDeleteClick = (row, event) => {
    event?.stopPropagation?.();
    if (isDocumentMasterRowUsed(row)) {
      showAlert({
        title: "Cannot delete",
        message: "This document category is in use and cannot be deleted.",
        type: "warning",
      });
      return;
    }
    setConfirmModal({ open: true, row });
  };

  const handleDeleteConfirm = async () => {
    const row = confirmModal.row;
    if (!row || !userData?.OrganizationId || !userData?.StaffId) {
      setConfirmModal({ open: false, row: null });
      return;
    }

    try {
      showLoader("Deleting document category...");
      const response = await deleteDocumentCategory(
        userData,
        row.DocumentCategoryId,
      );
      const resRow = firstSuccessStoryRow(response);

      if (isApiSuccess(response, resRow)) {
        showAlert({
          title: "Deleted",
          message:
            apiMessage(response, resRow) ||
            "Document category has been deleted successfully.",
          type: "success",
        });
        await loadCategories();
      } else {
        showAlert({
          title: "Error",
          message:
            apiMessage(response, resRow) || "Failed to delete document category.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error deleting document category", error);
      showAlert({
        title: "Error",
        message: error?.message || "Failed to delete document category.",
        type: "danger",
      });
    } finally {
      hideLoader();
      setConfirmModal({ open: false, row: null });
    }
  };

  if (access.status === "loading") {
    return null;
  }

  if (access.status === "denied") {
    if (embedded) {
      return (
        <div className="alert alert-warning mb-0" role="alert">
          <strong>Access restricted.</strong> {access.message}
        </div>
      );
    }
    return (
      <div className="user-mgmt-page qms-document-categories">
        <div className="alert alert-warning mt-3" role="alert">
          <strong>Access restricted.</strong> {access.message}
        </div>
      </div>
    );
  }

  const toolbarActions = [
    { icon: "fas fa-refresh", label: "Refresh", onClick: loadCategories },
    { icon: "fas fa-plus", label: "Add New Category", onClick: openAddModal },
  ];

  const isViewOnly = Boolean(editingRow && isDocumentMasterRowUsed(editingRow));

  return (
    <div className={`${embedded ? "" : "user-mgmt-page "}qms-document-categories${embedded ? " qms-document-categories--embedded" : ""}`}>
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      {embedded ? (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h6 className="mb-1 fw-semibold">Document Category</h6>
            <p className="text-muted small mb-0">
              Manage document category values used across QMS.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {toolbarActions.map((action) => (
              <button
                key={action.label}
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={action.onClick}
              >
                <i className={`${action.icon} me-1`} aria-hidden="true" />
                {action.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "QMS Config", path: "/superadmin/qms-config" },
            { label: "Document Category" },
          ]}
          actions={toolbarActions}
        />
      )}

      <>
      <DocumentMasterReportTable
        rows={rows}
        nameField="DocumentCategory"
        nameColumnLabel="Category Name"
        layoutStorageKey={layoutStorageKey}
        onEdit={openEditModal}
        onDelete={handleDeleteClick}
        loading={listLoading}
      />

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, row: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete document category"
        message={
          confirmModal.row
            ? `Do you want to delete category "${confirmModal.row.DocumentCategory}"?`
            : ""
        }
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass="btn-danger"
      />

      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isViewOnly
                    ? "View Document Category"
                    : editingRow
                      ? "Update Document Category"
                      : "Add Document Category"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {isViewOnly ? (
                  <div className="alert alert-warning py-2 mb-3" role="alert">
                    {QMS_DOCUMENT_CATEGORY_IN_USE_MESSAGE}
                  </div>
                ) : null}
                <div className="mb-3">
                  <label className="form-label">
                    Category <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.categoryName ? "is-invalid" : ""
                    }`}
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    maxLength={110}
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                  {errors.categoryName && (
                    <div className="invalid-feedback">{errors.categoryName}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Abbreviation <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.abbreviation ? "is-invalid" : ""
                    }`}
                    value={abbreviation}
                    onChange={(e) => handleAbbreviationChange(e.target.value)}
                    maxLength={QMS_DOCUMENT_ABBR_MAX_LENGTH}
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                  />
                  {!isViewOnly && !abbrTouched && categoryName.trim() ? (
                    <div className="form-text">Suggested from category name</div>
                  ) : null}
                  {errors.abbreviation && (
                    <div className="invalid-feedback">
                      {errors.abbreviation}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  {isViewOnly ? "Close" : "Cancel"}
                </button>
                {!isViewOnly ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                  >
                    {editingRow ? "Update" : "Save"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
      </>
    </div>
  );
};

export default QmsDocumentCategories;
